import styled from '@emotion/styled';

/**
 * BriefPage — Page 4 of the Kuutio print report.
 *
 * "1. BRIEF" + Kuutio objectives (left text + right blue callout box)
 * + "KUUTION 12 VAIHETTA" timeline (2 rows of 6 numbered yellow squares)
 *
 * All positions from IDML Spread_ucf.xml via idml-to-css.mjs.
 */
export default function BriefPage({
  instanceNumber = '3',
  topic = 'Kansainvälisten opiskelijoiden työllistyminen Suomeen',
}) {
  // Timeline phases — Day 1 (steps 1-6) and Day 2 (steps 7-12)
  const day1Steps = [
    { num: '1.', label: 'Brief', left: '40.0pt', width: '44.4pt' },
    { num: '2.', label: 'Toivottu\ntulevai-\nsuus', left: '90.1pt', width: '30.2pt' },
    { num: '3.', label: 'Skenaariot\nja tulevai-\nsuuskuvat', left: '126.4pt', width: '62.1pt' },
    { num: '4.', label: 'Nykytila\nja rajaus', left: '194.5pt', width: '68.4pt' },
    { num: '5.', label: 'Vaikutuskartta', left: '268.3pt', width: '89.8pt' },
    { num: '6.', label: 'Reflektio', left: '362.5pt', width: '36.8pt' },
  ];

  const day2Steps = [
    { num: '7.', label: 'Laskeu-\ntuminen', left: '429.4pt', width: '36.8pt' },
    { num: '8.', label: 'Vipu-\npisteet', left: '471.7pt', width: '63.1pt' },
    { num: '9.', label: 'Esteet ja\nmahdolli-\nsuudet', left: '540.8pt', width: '66.3pt' },
    { num: '10.', label: 'Entä\njos?', left: '613.2pt', width: '41.3pt' },
    { num: '11.', label: 'Ratkaisut', left: '660.6pt', width: '77.1pt' },
    { num: '12.', label: 'Lopetus', left: '743.4pt', width: '41.1pt' },
  ];

  return (
    <Page className="page brief-page">
      {/* Title: Otsikot style */}
      <TitleFrame>
        <Title>1. BRIEF</Title>
      </TitleFrame>
      <YellowRule />

      {/* Left body text: u16f at left:40pt top:79.1pt w:480.2pt — but source shows it's narrower, ~half page */}
      <BodyFrame>
        <Body>
          Aluksi kävimme läpi Kuutio-työskentelyn
          tavoitteet, säännöt, prosessin ja
          etenemisen.
        </Body>
        <Body>
          Sovimme noudattavamme turvallisen
          tilan periaatteita:
        </Body>
        <Body>
          1. Kuuntele, keskustele ja ole{'\n'}avoin oppimiselle.{'\n'}
          2. Kunnioita, älä oleta.{'\n'}
          3. Viihdytään yhdessä ja pidetään{'\n'}huolta toisistamme.
        </Body>
      </BodyFrame>

      {/* Middle column: "Kuution tavoitteena on:" — part of the same text frame */}
      <MiddleColumn>
        <Body><strong>Kuution tavoitteena on:</strong></Body>
        <BodyOl>
          <li>Vahvistaa osallistujien ennakointi- ja innovaatiokykyä.</li>
          <li>Tarjota julkiselle sektorille ketterä ja nopea tapa tarttua yhteiskunnallisiin aiheisiin.</li>
          <li>Tehdä yhteiskehittämisestä tehokkaampaa ja datapohjaista tekoälyn avulla.</li>
        </BodyOl>
      </MiddleColumn>

      {/* Right callout box: u2bcc Tellus@33% at left:548.5pt top:79.1pt w:265pt h:156.9pt */}
      <CalloutBox>
        <CalloutBorder />
        <CalloutContent>
          <Body>
            Lisäksi tavoitteenamme oli
            tarkastella kansainvälisten
            opiskelijoiden työllistymiseen
            liittyviä erilaisia tulevaisuuksia,
            muodostaa aihetta monipuolisesti
            tarkasteleva vaikutuskartta sekä
            löytää keskeisiä vipupisteitä ja
            ratkaisuehdotuksia.
          </Body>
        </CalloutContent>
      </CalloutBox>

      {/* "KUUTION 12 VAIHETTA" section */}
      <TimelineTitle>KUUTION <span style={{ fontWeight: 400, fontStyle: 'italic' }}>12 VAIHETTA</span></TimelineTitle>

      {/* Day 1 header */}
      <DayHeader style={{ left: '42.7pt', top: '352.8pt', width: '260.7pt' }}>
        <DayLabel>Päivä 1:</DayLabel> <DaySubtitle>NYKYTILA JA TULEVAISUUDET</DaySubtitle>
      </DayHeader>
      <DayLine style={{ left: '42.7pt', top: '367.8pt', width: '356.6pt' }} />

      {/* Day 2 header */}
      <DayHeader style={{ left: '430.2pt', top: '352.8pt', width: '260.7pt' }}>
        <DayLabel>Päivä 2:</DayLabel> <DaySubtitle>MUUTOKSET JA RATKAISUT</DaySubtitle>
      </DayHeader>
      <DayLine style={{ left: '430.2pt', top: '366.3pt', width: '352.7pt' }} />

      {/* Day 1 yellow squares */}
      {day1Steps.map((step, i) => (
        <StepGroup key={i}>
          <StepSquare style={{ left: step.left, top: '376.5pt', width: step.width, height: '58.4pt' }}>
            <StepNumber>{step.num}</StepNumber>
          </StepSquare>
          <StepLabel style={{ left: step.left, top: '441pt', width: step.width }}>
            {step.label}
          </StepLabel>
        </StepGroup>
      ))}

      {/* Day 2 yellow squares */}
      {day2Steps.map((step, i) => (
        <StepGroup key={i + 6}>
          <StepSquare style={{ left: step.left, top: '376.0pt', width: step.width, height: '58.4pt' }}>
            <StepNumber>{step.num}</StepNumber>
          </StepSquare>
          <StepLabel style={{ left: step.left, top: '441pt', width: step.width }}>
            {step.label}
          </StepLabel>
        </StepGroup>
      ))}

      <PageFooter>
        <FooterLeft><strong>Kuutio #{instanceNumber}:</strong> {topic}</FooterLeft>
        <FooterRight>4</FooterRight>
      </PageFooter>
    </Page>
  );
}

const Page = styled.div`
  width: 297mm;
  min-height: 210mm;
  background: #fff;
  color: #000;
  position: relative;
  font-family: 'Rational', sans-serif;
  box-sizing: border-box;
  overflow: hidden;

  @media print {
    break-after: page;
  }
`;

/* Title: Otsikot style — Bold Italic, 22pt, ALL CAPS */
const TitleFrame = styled.div`
  position: absolute;
  left: 42.5pt;
  top: 30.1pt;
  width: 153.1pt;
`;

const Title = styled.h1`
  font-size: 22pt;
  font-weight: 700;
  font-style: italic;
  text-transform: uppercase;
  margin: 0;
`;

const YellowRule = styled.div`
  position: absolute;
  left: 42.5pt;
  top: 58pt;
  width: calc(297mm - 42.5pt - 28.3pt);
  height: 3pt;
  background: #FBE300;
`;

/* Left body text column */
const BodyFrame = styled.div`
  position: absolute;
  left: 40.0pt;
  top: 79.1pt;
  width: 220pt;
`;

/* Middle column: objectives */
const MiddleColumn = styled.div`
  position: absolute;
  left: 280pt;
  top: 79.1pt;
  width: 240pt;
`;

const BodyOl = styled.ol`
  font-size: 11pt;
  font-weight: 400;
  line-height: 13pt;
  margin: 0;
  padding-left: 18pt;

  li {
    margin-bottom: 6pt;
  }
`;

const Body = styled.p`
  font-size: 11pt;
  font-weight: 400;
  line-height: 13pt;
  margin: 0 0 10pt 0;
  white-space: pre-line;
`;

/* Right callout box: Tellus @33% */
const CalloutBox = styled.div`
  position: absolute;
  left: 548.5pt;
  top: 79.1pt;
  width: 265.0pt;
  height: 156.9pt;
  display: flex;
`;

const CalloutBorder = styled.div`
  width: 5pt;
  background: #7CCDE4;
  flex-shrink: 0;
`;

const CalloutContent = styled.div`
  background: rgba(124, 205, 228, 0.33);
  padding: 14pt 16pt;
  flex: 1;
`;

/* Timeline section */
const TimelineTitle = styled.h2`
  position: absolute;
  left: 42.7pt;
  top: 305.2pt;
  font-size: 22pt;
  font-weight: 900;
  font-style: italic;
  margin: 0;
`;

const DayHeader = styled.div`
  position: absolute;
  font-size: 11pt;
`;

const DayLabel = styled.span`
  font-weight: 700;
  font-style: italic;
  text-transform: uppercase;
`;

const DaySubtitle = styled.span`
  font-weight: 400;
  font-style: italic;
  text-transform: uppercase;
`;

const DayLine = styled.div`
  position: absolute;
  height: 0.5pt;
  background: rgba(0, 0, 0, 0.3);
`;

const StepGroup = styled.div``;

const StepSquare = styled.div`
  position: absolute;
  background: #FBE300;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 6pt 8pt;
`;

const StepNumber = styled.span`
  font-size: 18pt;
  font-weight: 400;
  font-style: italic;
`;

const StepLabel = styled.div`
  position: absolute;
  font-size: 9pt;
  font-weight: 400;
  font-style: italic;
  line-height: 11pt;
  white-space: pre-line;
`;

const PageFooter = styled.div`
  position: absolute;
  bottom: 14pt;
  left: 42.5pt;
  right: 28.3pt;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 0.5pt solid rgba(0, 0, 0, 0.3);
  padding-top: 6pt;
`;

const FooterLeft = styled.span`
  font-size: 8pt;
  font-style: italic;
  font-weight: 400;
`;

const FooterRight = styled.span`
  font-size: 10pt;
  font-weight: 400;
`;
